import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, ListGroup, Row, Col, Button } from 'react-bootstrap';
import LoadingDots from './LoadingDots';
import { fetchAasEnvironment, fetchSubmodelDescriptors } from '../services/DppManagerService';
import { encodeBase64Url } from './Utils';
import { useIntl } from 'react-intl';
import Config from '../config/Config';
import { sortSubmodelDescriptorsByOrderReference } from './Utils';
import getLocalizedText from './Utils';
import SubmodelRegistryView from './SubmodelRegistryView';
import DppProductImage from './DppProductImage';
import '../css/DppAccessView.css';

// Registry constants
const REGISTRIES = ['PRIVATE', 'INTERMEDIATE', 'PUBLIC'];

const submodelOrderReference = Config.dpp.submodels.orderByIdShort;

const DppAccessView = ({ query }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { mainColor, secondaryColor } = Config.appearance;
  const [selectedAasId, setSelectedAasId] = useState(null);
  const [aasDescriptor, setAasDescriptor] = useState(null);
  const [assetInformation, setAssetInformation] = useState(null);
  const [submodelDescriptors, setSubmodelDescriptors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [shellAvailability, setShellAvailability] = useState({
    PRIVATE: false,
    INTERMEDIATE: false,
    PUBLIC: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!query) return;
      setError(null);
      setIsLoading(true);
      setAasDescriptor(null);
      setSubmodelDescriptors([]);
      setSelectedAasId(null);
      setShellAvailability({ PRIVATE: false, INTERMEDIATE: false, PUBLIC: false });
      setAssetInformation(null);

      try {
        const encodedSearchTerm = encodeBase64Url(query.trim());
        const aasEnv = await fetchAasEnvironment(encodedSearchTerm);

        if (!aasEnv) {
          setError('AAS Descriptor not found');
          return;
        }

        setAasDescriptor(aasEnv);
        setSelectedAasId(aasEnv.id);
        setAssetInformation(aasEnv.assetInformation);

        const smDescriptors = await fetchSubmodelDescriptors(aasEnv.id) || [];
        const sortedSmDescriptors = sortSubmodelDescriptorsByOrderReference(smDescriptors, submodelOrderReference);
        setSubmodelDescriptors(sortedSmDescriptors);

        const avail = { PRIVATE: false, INTERMEDIATE: false, PUBLIC: false };

        if (aasEnv.registries && typeof aasEnv.registries === 'object') {
          for (const r of REGISTRIES) {
            avail[r] = !!aasEnv.registries[r];
          }
        } else {
          for (const r of REGISTRIES) {
            if (r === 'PRIVATE') {
              avail.PRIVATE = true;
              continue;
            }
            const anySmInRegistry = smDescriptors.some(sm => {
              if (sm.registries && typeof sm.registries === 'object') {
                return !!sm.registries[r];
              }
              return false;
            });
            avail[r] = anySmInRegistry;
          }
        }

        setShellAvailability(avail);
      } catch (err) {
        console.error('Error fetching AAS descriptor:', err);
        setError(err.message || 'Error occurred while fetching data');
        setAasDescriptor(null);
        setSubmodelDescriptors([]);
        setSelectedAasId(null);
        setShellAvailability({ PRIVATE: false, INTERMEDIATE: false, PUBLIC: false });
        setAssetInformation(null);  
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

  }, [query]);

  return (
    <div className="dpp-access mt-5" style={{ 
      '--dpp-main-color': mainColor, 
      '--dpp-secondary-color': secondaryColor
    }}>

      {isLoading && <LoadingDots size={14} color={mainColor} />}

      {aasDescriptor && (
        <>
          {/* Header Section with Title, Description, Shell Badges (right), and Action Button */}
          <div className="product-heading-container d-flex justify-content-between align-items-start mb-4 flex-wrap gap-4">
            <div className="flex-grow-1">
              <h4 className="mb-1 fw-bold">
                {getLocalizedText(aasDescriptor.displayName, aasDescriptor.name, intl.locale)}
              </h4>
              {aasDescriptor.description && (
                <p className="text-muted mb-1">
                  {getLocalizedText(aasDescriptor.description, '', intl.locale)}
                </p>
              )}
              <div className="text-muted small">
                <code>
                  AAS-ID: {aasDescriptor.id}
                </code>
              </div>
            </div>

            <div className="d-flex flex-column align-items-end gap-4">
              {/* Shell Availability Badges */}
              <div>
                <strong className="me-3 text-nowrap">{intl.formatMessage({ id: 'available_in' })}</strong>
                {REGISTRIES.map(r => (
                  <Badge
                    key={r}
                    pill
                    bg={shellAvailability[r] ? 'success' : 'secondary'}
                    className="me-2"
                    title={`${r} registry`}
                  >
                    {r}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Two-column layout */}
          <Row>
            {/* Left Column (smaller) for Product Image and Asset Information */}
            <Col lg={3}>
              <div className="product-info-container" style={{
                    '--dpp-main-color': mainColor,
                    '--dpp-secondary-color': secondaryColor,
                }}>
                <div className="product-image-container">
                  <DppProductImage submodelDescriptors={submodelDescriptors} />
                </div>
                <div className="asset-info-container">
                  <h4>{intl.formatMessage({ id: 'asset_information' })}</h4>
                  {assetInformation ? (
                    <ListGroup className='ms-2'>
                      {assetInformation.globalAssetId && (
                        <ListGroup.Item className="border-0">
                          <b>Global Asset ID:</b><br />{assetInformation.globalAssetId}
                        </ListGroup.Item>
                      )}
                      {assetInformation.specificAssetIds && assetInformation.specificAssetIds.map((specificAsset, index) => (
                        <ListGroup.Item key={index} className="border-0">
                          <b>{specificAsset.name}:</b><br />{specificAsset.value}
                        </ListGroup.Item>
                      ))}
                      {assetInformation.assetType && (
                        <ListGroup.Item className="border-0"><b>Asset Type:</b><br />{assetInformation.assetType}</ListGroup.Item>
                      )}
                      {assetInformation.assetKind && (
                        <ListGroup.Item className="border-0"><b>Asset Kind:</b><br />{assetInformation.assetKind}</ListGroup.Item>
                      )}
                    </ListGroup>
                  ) : (
                    <p>No asset information available.</p>
                  )}
                </div>
                <Button
                  variant="primary"
                  onClick={() => navigate('/search?query=' + encodeBase64Url(query.trim()))}
                  className="text-nowrap my-4"
                >
                  {intl.formatMessage({ id: 'open_in_dpp_view' })}
                </Button>
              </div>

            </Col>

            {/* Right Column (larger) for Submodel Registry View */}
            <Col lg={9}>
              <SubmodelRegistryView
                aasDescriptor={aasDescriptor}
                submodelDescriptors={submodelDescriptors}
                shellAvailability={shellAvailability}
                registries={REGISTRIES}
                onShellAvailabilityChange={(newAvail) => setShellAvailability(newAvail)}
              />
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default DppAccessView;
